/**
 * Platform admin API client.
 *
 * Talks directly to the platform admin Lambda via X-Platform-Admin-Key.
 * Completely separate from the tenant API — no Cognito auth.
 * The base URL and admin key are stored in sessionStorage after "login".
 */
import axios from 'axios';

export interface PlatformTenant {
  id: number;
  slug: string;
  name: string;
  plan: 'starter' | 'professional' | 'enterprise';
  status: string;
  suspendedAt: string | null;
  createdAt: string;
}

export interface PlatformTenantDetail {
  tenant: PlatformTenant;
  subscription: {
    id: number;
    stripeSubscriptionId: string | null;
    status: string;
    currentPeriodEnd: string | null;
    quantity: number;
  } | null;
  features: { featureKey: string; enabled: boolean }[];
}

export interface ProvisionParams {
  tenantSlug: string;
  tenantName: string;
  adminEmail: string;
  adminFullName: string;
  plan: 'starter' | 'professional' | 'enterprise';
  initialUserCount?: number;
}

export interface MigrateResult {
  message: string;
  appliedMigrations: string[];
}

export interface MigrationStatus {
  applied: string[];
  pending: string[];
  currentVersion: string | null;
  totalFiles: number;
}

export interface PlatformAuditEntry {
  id: number;
  actor: string;
  action: string;
  targetSlug: string | null;
  details: Record<string, unknown> | null;
  createdAt: string;
}

const SESSION_URL_KEY = 'platform_admin_url';
const SESSION_KEY_KEY = 'platform_admin_key';
const SESSION_ACTOR_KEY = 'platform_admin_actor';

export function savePlatformCredentials(url: string, key: string, actor?: string) {
  sessionStorage.setItem(SESSION_URL_KEY, url);
  sessionStorage.setItem(SESSION_KEY_KEY, key);
  if (actor) sessionStorage.setItem(SESSION_ACTOR_KEY, actor);
}

export function clearPlatformCredentials() {
  sessionStorage.removeItem(SESSION_URL_KEY);
  sessionStorage.removeItem(SESSION_KEY_KEY);
  sessionStorage.removeItem(SESSION_ACTOR_KEY);
}

export function hasPlatformCredentials(): boolean {
  return !!sessionStorage.getItem(SESSION_KEY_KEY);
}

function makeClient() {
  const baseURL = (import.meta.env.VITE_API_URL as string ?? sessionStorage.getItem(SESSION_URL_KEY) ?? '').replace(/\/$/, '');
  const key = sessionStorage.getItem(SESSION_KEY_KEY) ?? '';
  const actor = sessionStorage.getItem(SESSION_ACTOR_KEY);
  return axios.create({
    baseURL,
    headers: {
      'Content-Type': 'application/json',
      'X-Platform-Admin-Key': key,
      ...(actor ? { 'X-Actor': actor } : {}),
    },
    timeout: 30_000,
  });
}

export const platformApi = {
  listTenants: () =>
    makeClient().get<PlatformTenant[]>('/platform/tenants').then((r) => r.data),

  getTenant: (slug: string) =>
    makeClient().get<PlatformTenantDetail>(`/platform/tenants/${slug}`).then((r) => r.data),

  provision: (params: ProvisionParams) =>
    makeClient().post<{ message: string }>('/platform/provision', params).then((r) => r.data),

  suspend: (slug: string) =>
    makeClient().post<{ message: string }>(`/platform/tenants/${slug}/suspend`).then((r) => r.data),

  unsuspend: (slug: string) =>
    makeClient().post<{ message: string }>(`/platform/tenants/${slug}/unsuspend`).then((r) => r.data),

  migrate: (slug: string, dryRun = false) =>
    makeClient().post<MigrateResult>(`/platform/tenants/${slug}/migrate`, { dryRun }).then((r) => r.data),

  getAuditLog: (params?: { slug?: string; action?: string; limit?: number; offset?: number }) =>
    makeClient().get<{ items: PlatformAuditEntry[]; limit: number; offset: number }>(
      '/platform/audit-log', { params }
    ).then((r) => r.data),

  getMigrationStatus: (slug: string) =>
    makeClient().get<MigrationStatus>(`/platform/tenants/${slug}/migrations`).then((r) => r.data),

  migratePublic: (dryRun = false) =>
    makeClient().post<{ message: string; appliedMigrations?: string[]; wouldApply?: string[] }>(
      '/platform/migrate-public', { dryRun }
    ).then((r) => r.data),
};
